import { playerEventTypes } from "../constants"

/*
  A ViewMedica video posts two kinds of message to the parent window, unasked
  — no vendor script or handshake is needed to receive them:

  - analytics, { analyticType, payload }: brochure printed, video loaded /
    started, percent and step reached, video end, and the playlist events
    (6-8). Every video plays as a playlist: a ~3 s branding intro ("Logo"),
    then the main video. Type 7 names the item that starts, which is how the
    intro is told apart from the main video.
  - player events, { event, payload }: onNavigate, onReady and onStateChange,
    the last with { state, duration?, currentTime? } on every play, pause,
    seek and end.

  Both formats are defined by the vendor library, a copy of which is kept at
  public/vm.js for reference (byte-identical to
  https://ondemand.viewmedica.com/lib/vm.js). It is no longer loaded: its
  player API was only needed for polling, which these messages replace.
*/

const VIEWMEDICA_ORIGIN = /ondemand\.viewmedica\.com/i

export const ANALYTIC_TYPES = Object.freeze({
  BROCHURE_PRINTED: 0,
  VIDEO_LOADED: 1,
  VIDEO_STARTED: 2,
  PERCENT_REACHED: 3,
  STEP_REACHED: 4,
  VIDEO_ENDED: 5,
  PLAYLIST_STARTED: 6,
  PLAYLIST_ITEM_REACHED: 7,
  PLAYLIST_ENDED: 8,
})

// ITEM_ENDED isn't in vm.js's PlayerState enum; the player sends it when an
// item (the intro or the main video) plays to its end.
export const PLAYER_STATES = Object.freeze({
  WAITING: -1,
  READY: 0,
  PLAYING: 1,
  PAUSED: 2,
  ITEM_ENDED: 9,
})

export const ANALYTIC_TYPE_TO_EVENT = Object.freeze({
  [ANALYTIC_TYPES.BROCHURE_PRINTED]: playerEventTypes.BROCHURE_PRINTED,
  [ANALYTIC_TYPES.VIDEO_LOADED]: playerEventTypes.VIDEO_LOADED,
  [ANALYTIC_TYPES.VIDEO_STARTED]: playerEventTypes.VIDEO_STARTED,
  [ANALYTIC_TYPES.PERCENT_REACHED]: playerEventTypes.PERCENT_REACHED,
  [ANALYTIC_TYPES.STEP_REACHED]: playerEventTypes.STEP_REACHED,
  [ANALYTIC_TYPES.VIDEO_ENDED]: playerEventTypes.VIDEO_ENDED,
})

export const PLAYER_STATE_TO_EVENT = Object.freeze({
  [PLAYER_STATES.WAITING]: playerEventTypes.PLAYER_WAITING,
  [PLAYER_STATES.READY]: playerEventTypes.PLAYER_READY,
  [PLAYER_STATES.PLAYING]: playerEventTypes.PLAYER_PLAYED,
  [PLAYER_STATES.PAUSED]: playerEventTypes.PLAYER_PAUSED,
})

/*
  The message's data if it came from this iframe's ViewMedica player, else
  null. A report can embed several videos, each posting to the same window,
  so the source check is what keeps one video's messages off another.
*/
export const getViewMediaMessage = (event, iframe) => {
  if (!event.origin || !VIEWMEDICA_ORIGIN.test(event.origin)) return null
  if (!iframe || event.source !== iframe.contentWindow) return null
  return event.data && typeof event.data === "object" ? event.data : null
}

// ViewMedica's public share page for our account (11307, English). Opening a
// video for the user goes here; the stored link is the embed URL, which is
// only for the player.
const VIEWMEDICA_SHARE_URL = "https://my.viewmedica.com/share/11307/en/"

// The share page for a stored video link: the video id is the last segment of
// the link's path. Null when the link can't be parsed or has no path.
export const getViewMedicaShareUrl = (link) => {
  try {
    const videoId = new URL(link).pathname.split("/").filter(Boolean).pop()
    return videoId
      ? `${VIEWMEDICA_SHARE_URL}${encodeURIComponent(videoId)}`
      : null
  } catch {
    return null
  }
}
